import { Component, Input, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ApiEstimativaService } from 'src/app/modules/shared/services/api-estimativa.service';
import { DataService } from 'src/app/modules/shared/services/data.service';
import { LanguageService } from 'src/app/modules/shared/services/language.service';
import { InfoBarWithStatusService } from 'src/app/modules/shared/services/info-bar-with-status.service';
import { Permission } from 'src/app/modules/shared/security/permission';
import { EstimationState } from 'src/app/models/EstimationState';

interface ImputationRow {
    sectionType: string;
    sectionName: string;
    groupName: string;
    estimatedHours: number;
    actualHours: number | null;
}

interface SummaryRow {
    name: string;
    estimated: number;
    actual: number;
    difference: number | null;
    expandable: boolean;
    items?: ImputationRow[];
    detailDataSource?: MatTableDataSource<ImputationRow>;
}

@Component({
    standalone: false,
    selector: 'app-imputacion-real',
    templateUrl: './imputacion-real.component.html',
    styleUrls: ['./imputacion-real.component.css']
})
export class ImputacionRealComponent implements OnInit {

    @Input() sideNavStatus!: boolean;

    variables: any = {};
    estimate: any;
    summaryDTO: any;
    imputationRows: ImputationRow[] = [];
    isSaving: boolean = false;
    isLoading: boolean = true;
    hasExistingData: boolean = false;


    Permission = Permission

    summaryColumns: string[] = ['name', 'estimated', 'actual', 'difference'];
    detailColumns: string[] = ['description', 'estimated', 'actual', 'difference'];
    summaryDataSource = new MatTableDataSource<SummaryRow>([]);

    private expandedSections = new Set<string>();

    constructor(
        private dataService: DataService,
        private estimativaService: ApiEstimativaService,
        private languageService: LanguageService,
        private infoBar: InfoBarWithStatusService
    ) { }

    public estimationState = EstimationState;

    ngOnInit(): void {
        this.languageService.translations$.subscribe((vars: any) => {
            this.variables = vars;
        });

        this.loadEstimateAndImputations(this.resolveEstimateId());
    }

    private resolveEstimateId(): string {
        const estimate = this.dataService.getEstimate();
        return typeof estimate === 'object' ? this.dataService.getEstimateId() : estimate;
    }

    private loadEstimateAndImputations(estimateId: string): void {
        this.isLoading = true;

        this.estimativaService.getEstimateById(estimateId).subscribe({
            next: (estimate: any) => {
                this.estimate = estimate;
                this.calculateAndBuild(estimate, estimateId);
            },
            error: (err: any) => {
                console.error('Error loading estimate:', err);
                this.isLoading = false;
            }
        });
    }

    private calculateAndBuild(estimate: any, estimateId: string): void {
        this.estimativaService.calculate(estimateId).subscribe({
            next: (result: any) => {
                this.summaryDTO = result;
                this.extractSections(this.summaryDTO);
                this.loadExistingImputations(estimateId);
            },
            error: (err: any) => {
                console.error('Error calculating estimate:', err);
                this.isLoading = false;
            }
        });
    }

    /**
     * Extract sections using the EXACT same data structure as ResultComponent.
     * Field names match: riskTable, configTable, paramsTable, macrosTable, tasksTable
     */
    private extractSections(result: any): void {
        this.imputationRows = [];

        // 1. Risco associado ao tipo de projeto (projectType)
        if (result?.risk.rows) {
            this.imputationRows.push({
                sectionType: this.variables.riskOfProject || 'Risco associado ao tipo de projeto',
                sectionName: result.risk.rows[0].name || '',
                groupName: this.variables.risk || 'Riesgo',
                estimatedHours: result.risk.totalEffort || 0,
                actualHours: null
            });
        }

        // 2. Configurações (configurations)
        if (result?.configurations && result?.configurations.rows && result.configurations.rows.length > 0) {
            for (const item of result.configurations.rows) {
                this.imputationRows.push({
                    sectionType: this.variables.configurations || 'Configurações',
                    sectionName: item.name || '',
                    groupName: this.variables.configurations || 'Configurações',
                    estimatedHours: item.complexity || 0,
                    actualHours: null
                });
            }
        }

        // 3. Parâmetros (parameters) — only if present
        if (result?.parameters && result?.parameters.rows && result.parameters.rows.length > 0) {
            for (const item of result.parameters.rows) {
                this.imputationRows.push({
                    sectionType: this.variables.parameters || 'Parâmetros',
                    sectionName: item.name || '',
                    groupName: this.variables.parameters || 'Parâmetros',
                    estimatedHours: item.complexity || 0,
                    actualHours: null
                });
            }
        }

        // 4. Macro atividades (macroActivities -> macroActivityResults)
        if (result?.macroActivities && result?.macroActivities?.rows && result.macroActivities.rows.length > 0) {
            for (const macro of result.macroActivities.rows) {
                this.imputationRows.push({
                    sectionType: this.variables.macroActivities || 'Macro atividades',
                    sectionName: macro.name || '',
                    groupName: macro.metodology || '',
                    estimatedHours: macro.complexity || 0,
                    actualHours: null
                })
            }
        }

        // 5. Tarefas de projeto (subTasks)
        if (result?.projectActivities && result?.projectActivities?.rows && result.projectActivities.rows.length > 0) {
            for (const task of result.projectActivities.rows) {
                this.imputationRows.push({
                    sectionType: this.variables.projectTasks || 'Tarefas de projeto',
                    sectionName: task.task ? (task.task + (task.subTask ? ' - ' + task.subTask : '')) : (task.subTask || ''),
                    groupName: task.epic || task.task || '',
                    estimatedHours: task.complexity || 0,
                    actualHours: null
                });
            }
        }
    }

    private loadExistingImputations(estimateId: string): void {
        this.estimativaService.getImputation(estimateId).subscribe({
            next: (imputation: any) => {
                if (imputation && imputation.imputations) {
                    this.hasExistingData = true;
                    for (const saved of imputation.imputations) {
                        const match = this.imputationRows.find(
                            r => r.sectionType === saved.sectionType && r.sectionName === saved.sectionName
                        );
                        if (match) {
                            match.actualHours = saved.actualHours;
                        }
                    }
                }
                this.refreshSummaryDataSource();
                this.isLoading = false;
            },
            error: () => {
                // No existing imputations — that's fine
                this.refreshSummaryDataSource();
                this.isLoading = false;
            }
        });
    }

    // --- Toggle / expand ---
    toggleSection(name: string): void {
        if (this.expandedSections.has(name)) {
            this.expandedSections.delete(name);
        } else {
            this.expandedSections.add(name);
        }
    }

    hasExpandableSections(): boolean {
        return this.getExpandableSectionNames().length > 0;
    }

    areAllSectionsExpanded(): boolean {
        const expandableSectionNames = this.getExpandableSectionNames();
        if (expandableSectionNames.length === 0) {
            return false;
        }

        return expandableSectionNames.every((name) => this.expandedSections.has(name));
    }

    toggleAllSections(): void {
        const expandableSectionNames = this.getExpandableSectionNames();
        if (expandableSectionNames.length === 0) {
            return;
        }

        if (this.areAllSectionsExpanded()) {
            this.expandedSections.clear();
            return;
        }

        this.expandedSections = new Set(expandableSectionNames);
    }

    isSectionExpanded(name: string): boolean {
        return this.expandedSections.has(name);
    }

    isLastRow(dataSource: any, index: number): boolean {
        return index === dataSource.filteredData.length - 1;
    }

    /**
     * Called on every input change in the detail sub-table.
     * Recalculates actual/difference for the parent summary row and the total row.
     */
    onActualHoursChange(): void {
        const data = this.summaryDataSource.data;
        for (const row of data) {
            if (row.expandable && row.items) {
                row.actual = row.items.reduce((sum, r) => sum + (r.actualHours || 0), 0);
                row.difference = row.estimated - row.actual;
            }
        }
        // Update total row (last row)
        const totalRow = data[data.length - 1];
        if (totalRow && !totalRow.expandable) {
            totalRow.estimated = this.getTotalEstimated();
            totalRow.actual = this.getTotalActual();
            totalRow.difference = totalRow.estimated - totalRow.actual;
        }
        // Trigger mat-table refresh
        this.summaryDataSource.data = [...data];
    }

    // --- Data grouping ---
    private refreshSummaryDataSource(): void {
        const groups = new Map<string, ImputationRow[]>();
        for (const row of this.imputationRows) {
            if (!groups.has(row.sectionType)) {
                groups.set(row.sectionType, []);
            }
            groups.get(row.sectionType)!.push(row);
        }

        const summaryRows: SummaryRow[] = [];
        groups.forEach((items, name) => {
            const totalEstimated = items.reduce((sum, r) => sum + r.estimatedHours, 0);
            const totalActual = items.reduce((sum, r) => sum + (r.actualHours || 0), 0);
            summaryRows.push({
                name: name,
                estimated: totalEstimated,
                actual: totalActual,
                difference: totalEstimated - totalActual,
                expandable: true,
                items: items,
                detailDataSource: new MatTableDataSource(items)
            });
        });

        // Total row
        const totalEst = this.getTotalEstimated();
        const totalAct = this.getTotalActual();
        summaryRows.push({
            name: this.variables.total || 'Total',
            estimated: totalEst,
            actual: totalAct,
            difference: totalEst - totalAct,
            expandable: false
        });

        this.summaryDataSource = new MatTableDataSource(summaryRows);
    }

    getTotalEstimated(): number {
        return this.imputationRows.reduce((sum, row) => sum + (row.estimatedHours || 0), 0);
    }

    getTotalActual(): number {
        return this.imputationRows.reduce((sum, row) => sum + (row.actualHours || 0), 0);
    }

    private getExpandableSectionNames(): string[] {
        return this.summaryDataSource.data
            .filter((row) => row.expandable)
            .map((row) => row.name);
    }

    saveImputations(): void {
        this.isSaving = true;

        const estimateId = this.resolveEstimateId();

        const request = {
            estimateId: estimateId,
            imputations: this.imputationRows.map(row => ({
                sectionType: row.sectionType,
                sectionName: row.sectionName,
                groupName: row.groupName,
                estimatedHours: row.estimatedHours,
                actualHours: row.actualHours || 0
            })),
            totalActualHours: this.getTotalActual()
        };

        this.estimativaService.saveImputation(request).subscribe({
            next: () => {
                this.isSaving = false;
                this.hasExistingData = true;
                this.infoBar.showSnackbar(this.variables.imputationSavedSuccess || 'Imputación guardada correctamente', ['successful-snackbar']);
            },
            error: (err: any) => {
                this.isSaving = false;
                console.error('Error saving imputation:', err);
                this.infoBar.showSnackbar(this.variables.imputationSaveError || 'Error al guardar la imputación', ['error-snackbar']);
            }
        });
    }
}
